import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Download, FileBarChart, History, Package, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const FuelHistoryDialogSection = ({ ctx }) => {
  const {
    detailedFuelHistory,
    fuelHistoryDialogOpen,
    loadingDetailedHistory,
    selectedEquipmentForHistory,
    setFuelHistoryDialogOpen
  } = ctx;
  return (
    <>
      <Dialog open={fuelHistoryDialogOpen} onOpenChange={setFuelHistoryDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-primary flex items-center space-x-2">
              <History className="w-5 h-5" />
              <span>FUEL HISTORY - {selectedEquipmentForHistory?.name}</span>
            </DialogTitle>
          </DialogHeader>
          
          {selectedEquipmentForHistory && (
            <div className="space-y-6 mt-4">
              {/* Equipment Info Card */}
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 p-4 rounded-sm border border-amber-200">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <p className="text-xs uppercase tracking-widest text-amber-700 mb-1">Equipment</p>
                    <p className="font-semibold">{selectedEquipmentForHistory.name}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-widest text-amber-700 mb-1">Registration / ID</p>
                    <p className="font-mono font-semibold">{selectedEquipmentForHistory.equipment_id || selectedEquipmentForHistory.registration_number || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-widest text-amber-700 mb-1">Type</p>
                    <p className="font-medium">{selectedEquipmentForHistory.type || 'Equipment'}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-widest text-amber-700 mb-1">Owner</p>
                    <p className="font-medium">{selectedEquipmentForHistory.owner_name || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Fuel Summary */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {(() => {
                  const totalDiesel = detailedFuelHistory.filter(o => o.fuel_type === 'Diesel' || o.fuel_type === 'HSD').reduce((sum, o) => sum + (o.quantity || 0), 0);
                  const totalPetrol = detailedFuelHistory.filter(o => o.fuel_type === 'Petrol').reduce((sum, o) => sum + (o.quantity || 0), 0);
                  const totalIssues = detailedFuelHistory.length;
                  const avgPerIssue = totalIssues > 0 ? (totalDiesel + totalPetrol) / totalIssues : 0;
                  
                  return (
                    <>
                      <div className="text-center p-4 bg-amber-100 rounded-sm">
                        <p className="text-xs uppercase tracking-widest text-amber-700 mb-1">Total Diesel</p>
                        <p className="font-mono font-bold text-2xl text-amber-700">{totalDiesel.toFixed(2)} L</p>
                      </div>
                      <div className="text-center p-4 bg-green-100 rounded-sm">
                        <p className="text-xs uppercase tracking-widest text-green-700 mb-1">Total Petrol</p>
                        <p className="font-mono font-bold text-2xl text-green-700">{totalPetrol.toFixed(2)} L</p>
                      </div>
                      <div className="text-center p-4 bg-blue-100 rounded-sm">
                        <p className="text-xs uppercase tracking-widest text-blue-700 mb-1">Total Issues</p>
                        <p className="font-mono font-bold text-2xl text-blue-700">{totalIssues}</p>
                      </div>
                      <div className="text-center p-4 bg-purple-100 rounded-sm">
                        <p className="text-xs uppercase tracking-widest text-purple-700 mb-1">Avg per Issue</p>
                        <p className="font-mono font-bold text-2xl text-purple-700">{avgPerIssue.toFixed(2)} L</p>
                      </div>
                    </>
                  );
                })()}
              </div>

              {/* Detailed History Table */}
              <div>
                <h4 className="font-heading font-bold text-lg text-slate-800 mb-3 flex items-center">
                  <FileBarChart className="w-5 h-5 mr-2" />
                  Complete Fueling History
                </h4>
                
                {loadingDetailedHistory ? (
                  <div className="text-center py-8">
                    <RefreshCw className="w-8 h-8 animate-spin mx-auto text-primary" />
                    <p className="text-slate-500 mt-2">Loading history...</p>
                  </div>
                ) : detailedFuelHistory.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-slate-100">
                          <th className="text-left px-3 py-2 font-semibold">S.No</th>
                          <th className="text-left px-3 py-2 font-semibold">Date</th>
                          <th className="text-left px-3 py-2 font-semibold">Fuel Type</th>
                          <th className="text-right px-3 py-2 font-semibold">Quantity (L)</th>
                          <th className="text-left px-3 py-2 font-semibold">Issued By</th>
                          <th className="text-left px-3 py-2 font-semibold">Purpose</th>
                          <th className="text-left px-3 py-2 font-semibold">Remarks</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detailedFuelHistory.map((issue, idx) => (
                          <tr key={issue.id} className="border-b border-slate-100 hover:bg-slate-50">
                            <td className="px-3 py-2 font-mono text-slate-500">{idx + 1}</td>
                            <td className="px-3 py-2 font-mono font-medium">{issue.date}</td>
                            <td className="px-3 py-2">
                              <span className={`px-2 py-1 rounded-sm text-xs font-medium ${
                                issue.fuel_type === 'Diesel' || issue.fuel_type === 'HSD' 
                                  ? 'bg-amber-100 text-amber-700' 
                                  : 'bg-green-100 text-green-700'
                              }`}>
                                {issue.fuel_type || 'Diesel'}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-right font-mono font-bold text-primary">{issue.quantity?.toFixed(2)}</td>
                            <td className="px-3 py-2 text-slate-600">{issue.created_by_name || issue.issued_to || '-'}</td>
                            <td className="px-3 py-2 text-slate-600">{issue.purpose || '-'}</td>
                            <td className="px-3 py-2 text-slate-500 text-xs">{issue.remarks || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="bg-slate-100 font-semibold">
                          <td colSpan="3" className="px-3 py-2 text-right">TOTAL:</td>
                          <td className="px-3 py-2 text-right font-mono text-primary">
                            {detailedFuelHistory.reduce((sum, o) => sum + (o.quantity || 0), 0).toFixed(2)} L
                          </td>
                          <td colSpan="3"></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <Package className="w-12 h-12 mx-auto mb-2 text-slate-400" />
                    <p>No fuel issues recorded for this equipment</p>
                  </div>
                )}
              </div>

              {/* Export Button */}
              {detailedFuelHistory.length > 0 && (
                <div className="flex justify-end pt-4 border-t border-slate-200">
                  <button
                    onClick={() => {
                      // Export this equipment's fuel history
                      let csvContent = "data:text/csv;charset=utf-8,";
                      csvContent += `Fuel History - ${selectedEquipmentForHistory.name}\n`;
                      csvContent += `Registration: ${selectedEquipmentForHistory.equipment_id || selectedEquipmentForHistory.registration_number || 'N/A'}\n`;
                      csvContent += `Generated: ${new Date().toLocaleString()}\n\n`;
                      csvContent += `S.No,Date,Fuel Type,Quantity (L),Issued By,Purpose,Remarks\n`;
                      
                      detailedFuelHistory.forEach((issue, idx) => {
                        csvContent += `${idx + 1},"${issue.date}","${issue.fuel_type || 'Diesel'}",${issue.quantity?.toFixed(2) || 0},"${issue.created_by_name || issue.issued_to || '-'}","${issue.purpose || '-'}","${issue.remarks || '-'}"\n`;
                      });
                      
                      csvContent += `\nTotal,,,"${detailedFuelHistory.reduce((sum, o) => sum + (o.quantity || 0), 0).toFixed(2)} L"\n`;
                      
                      const encodedUri = encodeURI(csvContent);
                      const link = document.createElement("a");
                      link.setAttribute("href", encodedUri);
                      link.setAttribute("download", `Fuel_History_${selectedEquipmentForHistory.name?.replace(/[^a-z0-9]/gi, '_')}_${new Date().toISOString().split('T')[0]}.csv`);
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                      toast.success("Fuel history exported");
                    }}
                    className="btn-outline flex items-center space-x-2"
                  >
                    <Download className="w-4 h-4" />
                    <span>Export History</span>
                  </button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default FuelHistoryDialogSection;
